import { useCallback } from 'react';

import { useAdminCompanyController_addUser } from '@api-client/generated/AdminCompanyController/addUser';
import { useAdminCompanyController_listMemberships } from '@api-client/generated/AdminCompanyController/listMemberships';
import { Schemas } from '@api-client/generated/types';
import { useQueryClient } from '@tanstack/react-query';

import { useDetachMember } from './useDetachMember';

export { LAST_COMPANY_USER_ERROR } from './useDetachMember';

type AccessRights = Schemas.AccessRights;
type Membership = Schemas.AdminMembershipRecordDto;

/**
 * Thin wrapper over the generated membership read + add mutation for a single
 * company, plus the shared detach unit. Keeps the Team page away from endpoint
 * request/response shapes, and centralises cache invalidation so
 * removability/rights stay fresh.
 */
export const useCompanyTeam = (companyId: string) => {
  const queryClient = useQueryClient();

  const membershipsQuery = useAdminCompanyController_listMemberships({
    params: { companyId },
    config: { enabled: !!companyId },
  });

  const addMutation = useAdminCompanyController_addUser();
  const { detach: detachMember, isDetaching } = useDetachMember();

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [
          'AdminCompanyController_listMemberships',
          { parameter: { companyId } },
        ],
      }),
    [queryClient, companyId]
  );

  const attach = useCallback(
    (userId: string, accessRights: AccessRights) =>
      addMutation
        .mutateAsync({
          parameter: { id: companyId },
          requestBody: { userId, accessRights },
        })
        .finally(() => {
          // Refetch regardless of outcome: on success the new row appears, and
          // on `user_exists` the roster is out of date and needs reconciling.
          void invalidate();
        }),
    [addMutation, companyId, invalidate]
  );

  const detach = useCallback(
    (userId: string) => detachMember(companyId, userId),
    [detachMember, companyId]
  );

  const members: Membership[] = membershipsQuery.data?.records ?? [];

  return {
    members,
    isLoading: membershipsQuery.isLoading,
    isError: membershipsQuery.isError,
    refetch: membershipsQuery.refetch,
    attach,
    detach,
    isAttaching: addMutation.isPending,
    isDetaching,
  };
};
